import { isTauriSync } from "./tauri";
import { isCapacitor } from "./capacitor";

type Settings = {
  server_url: string;
  token: string | null;
  device_label: string;
  notification_sound: boolean;
};

const STORAGE_KEY = "swift.settings.v1";

const DEFAULTS: Settings = {
  server_url: "https://swift.eco-logisch.com",
  token: null,
  device_label: typeof navigator !== "undefined" ? hostnameGuess() : "desktop",
  notification_sound: true,
};

function hostnameGuess(): string {
  // Geen access tot Windows-hostname vanaf renderer; we vragen 't bij eerste login.
  return "desktop";
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSettings(patch: Partial<Settings>): Settings {
  const next = { ...loadSettings(), ...patch };
  // In PWA-mode (browser, niet native) bewaren we het Bearer-token NIET
  // in localStorage — auth gaat via httpOnly-cookie zodat eventuele XSS
  // niets te stelen heeft. Native shells (Tauri-MSI én Android-APK)
  // hebben geen cookie-flow en gebruiken Bearer uit localStorage.
  const native = isTauriSync() || isCapacitor();
  const toWrite = native ? next : { ...next, token: null };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toWrite));
  if (isCapacitor()) void mirrorToNative(next);
  return next;
}

/** Eenmalige mirror bij app-start: bestaande installaties hebben hun
 *  token al in localStorage van vóór het mirror-mechanisme; zonder deze
 *  sync zou de native preview-fetch pas na de eerstvolgende login werken. */
export function syncNativeSettingsMirror(): void {
  if (!isCapacitor()) return;
  void mirrorToNative(loadSettings());
}

/** Spiegelt server-URL en token naar Capacitor Preferences (SharedPrefs
 *  "CapacitorStorage"), zodat de native SwiftMessagingService bij een
 *  binnenkomende FCM-wakeup de melding-preview bij onze eigen server kan
 *  ophalen. localStorage is voor die service onbereikbaar — de WebView
 *  draait dan niet eens. Fire-and-forget; falen is geen ramp (de app
 *  valt dan terug op de generieke "Nieuw bericht"-melding). */
async function mirrorToNative(s: Settings): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: "swift_server_url", value: s.server_url });
    if (s.token) {
      await Preferences.set({ key: "swift_api_token", value: s.token });
    } else {
      await Preferences.remove({ key: "swift_api_token" });
    }
  } catch {
    /* */
  }
}

export function clearToken(): void {
  saveSettings({ token: null });
}

/* ── Verborgen gesprekken ───────────────────────────────────────────
 * Een gesprek "sluiten" verwijdert het niet uit de DB (de geschiedenis
 * blijft staan), het verbergt het alleen uit de zijbalk-lijst. Terug-
 * halen kan op twee manieren: de persoon opnieuw aanklikken in
 * "Online", óf — cruciaal voor afzenders die nooit online zijn, zoals
 * de meldingen-bot — doordat er een NIEUW bericht binnenkomt. Daarvoor
 * onthouden we per verborgen gesprek de last_message_at van het moment
 * van verbergen; wijkt de actuele waarde daarvan af, dan is er iets
 * nieuws en hoort het gesprek weer in de lijst (zie Sidebar).
 *
 * v2-formaat: { [conversationId]: last_message_at | "" }. "" betekent
 * "verborgen toen er nog geen berichten waren". Het oude v1-formaat
 * (kale id-array) wordt gemigreerd met marker null = "onbekend"; de
 * Sidebar vult die lazy in met de dan-actuele last_message_at zodat
 * ook oude verborgen gesprekken bij het eerstvolgende bericht terug-
 * komen. Lokaal opgeslagen, dus per-apparaat.
 */
const HIDDEN_KEY = "swift.hidden_conversations.v2";
const HIDDEN_KEY_V1 = "swift.hidden_conversations.v1";

export function getHiddenConversations(): Map<number, string | null> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, string | null>;
      const map = new Map<number, string | null>();
      for (const [k, v] of Object.entries(obj)) {
        const id = Number.parseInt(k, 10);
        if (Number.isFinite(id)) map.set(id, typeof v === "string" ? v : null);
      }
      return map;
    }
    // Migratie vanaf v1: alleen id's bekend, marker onbekend (null).
    const rawV1 = localStorage.getItem(HIDDEN_KEY_V1);
    if (rawV1) {
      const arr = JSON.parse(rawV1) as unknown;
      const map = new Map<number, string | null>();
      if (Array.isArray(arr)) {
        for (const x of arr) if (typeof x === "number") map.set(x, null);
      }
      persistHidden(map);
      localStorage.removeItem(HIDDEN_KEY_V1);
      return map;
    }
    return new Map();
  } catch {
    return new Map();
  }
}

function persistHidden(map: Map<number, string | null>): void {
  localStorage.setItem(HIDDEN_KEY, JSON.stringify(Object.fromEntries(map)));
}

/** Verbergt een gesprek. `lastMessageAt` is de actuele last_message_at
 *  ("" als er nog geen berichten zijn) — de vergelijkingsbasis waarmee
 *  een later binnenkomend bericht het gesprek weer zichtbaar maakt. */
export function hideConversation(id: number, lastMessageAt: string | null): void {
  const map = getHiddenConversations();
  map.set(id, lastMessageAt ?? "");
  persistHidden(map);
}

export function unhideConversation(id: number): void {
  const map = getHiddenConversations();
  map.delete(id);
  persistHidden(map);
}

/** Vult de marker in voor een gemigreerde v1-entry (marker null) zodat
 *  het gesprek verborgen blijft tot het eerstvolgende nieuwe bericht. */
export function setHiddenMarker(id: number, lastMessageAt: string | null): void {
  const map = getHiddenConversations();
  if (!map.has(id)) return;
  map.set(id, lastMessageAt ?? "");
  persistHidden(map);
}

/* ── Gedempte gesprekken ────────────────────────────────────────────
 * Een gedempt kanaal/gesprek geeft geen geluid, taskbar-flash of
 * toast bij nieuwe berichten. Het blijft wel zichtbaar en de
 * ongelezen-badge blijft tellen — je mist niets, je wordt alleen
 * niet onderbroken. Lokaal, per-apparaat.
 */
const MUTED_KEY = "swift.muted_conversations.v1";

export function getMutedConversationIds(): Set<number> {
  try {
    const raw = localStorage.getItem(MUTED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(arr) ? arr.filter((x): x is number => typeof x === "number") : []);
  } catch {
    return new Set();
  }
}

function persistMuted(set: Set<number>): void {
  localStorage.setItem(MUTED_KEY, JSON.stringify([...set]));
}

export function setConversationMuted(id: number, muted: boolean): void {
  const set = getMutedConversationIds();
  if (muted) set.add(id);
  else set.delete(id);
  persistMuted(set);
}
